import React, { useCallback, useEffect, useMemo, useState } from "react";
import styled from "styled-components";

import { Button, Input, Tag } from "../../../../design-system";
import {
  BookingConfig,
  BookingService,
  TimeRange,
  useBookingConfig,
  saveBookingConfig,
  listBookings,
  adminCancelBooking,
  BookingSummary,
  bookingSyncErrorText,
} from "../../../../database/booking/bookingApi";
import GoogleCalendarConnectPanel from "./GoogleCalendarConnectPanel";
import ZoomConnectPanel from "./ZoomConnectPanel";

type Props = {
  communityId?: string;
  isActive?: boolean;
};

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--space-5);
`;
const Intro = styled.p`
  font-family: var(--font-sans);
  font-size: var(--font-size-sm);
  color: var(--color-text-secondary);
  line-height: 1.6;
  margin: 0;
  max-width: 720px;
`;
const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
`;
const SectionTitle = styled.h3`
  font-family: var(--font-sans);
  font-size: var(--font-size-md);
  font-weight: var(--font-weight-semibold);
  color: var(--color-text);
  margin: 0;
`;
const Row = styled.div`
  display: flex;
  gap: var(--space-3);
  align-items: flex-end;
  flex-wrap: wrap;
`;
const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
  min-width: 140px;
`;
const Label = styled.label`
  font-family: var(--font-sans);
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-medium);
  color: var(--color-text-secondary);
`;
const ToggleHint = styled.span`
  font-family: var(--font-sans);
  font-size: var(--font-size-xs);
  color: var(--color-text-secondary);
  line-height: 1.5;
  max-width: 360px;
`;
// joinBeforeHost is an additive, Zoom-only field on service.location. The
// shared BookingLocation type does not declare it, so widen it locally.
type ZoomBookingLocation = BookingService["location"] & { joinBeforeHost?: boolean };
const ServiceCard = styled.div`
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  padding: var(--space-4);
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
`;
const DayRow = styled.div`
  display: grid;
  grid-template-columns: 70px 1fr;
  gap: var(--space-3);
  align-items: center;
`;
const DayLabel = styled.span`
  font-family: var(--font-sans);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  color: var(--color-text);
  text-transform: capitalize;
`;
const Select = styled.select`
  font-family: var(--font-sans);
  font-size: var(--font-size-sm);
  padding: var(--space-2) var(--space-3);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-sm);
  background: var(--color-surface);
  color: var(--color-text);
`;
const Warn = styled.div`
  font-family: var(--font-sans);
  font-size: var(--font-size-xs);
  color: var(--color-warning-text, #9a6700);
`;
const Table = styled.div`
  display: flex;
  flex-direction: column;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  overflow: hidden;
`;
const BodyRow = styled.div`
  display: grid;
  grid-template-columns: 1.4fr 1.6fr 1fr auto;
  gap: var(--space-3);
  padding: var(--space-3) var(--space-4);
  align-items: center;
  border-top: 1px solid var(--color-border);
  font-family: var(--font-sans);
  font-size: var(--font-size-sm);
  color: var(--color-text);
`;
const Muted = styled.span`
  color: var(--color-text-secondary);
  font-size: var(--font-size-xs);
`;
// The service name plus, when the meeting sync failed, the flag under it.
const ServiceCell = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: var(--space-1);
`;
const Empty = styled.div`
  padding: var(--space-6);
  text-align: center;
  font-family: var(--font-sans);
  font-size: var(--font-size-sm);
  color: var(--color-text-secondary);
`;

const DAYS = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"] as const;

const rangesToText = (ranges?: TimeRange[]): string =>
  (ranges || []).map((r) => `${r.start}-${r.end}`).join(", ");

const textToRanges = (text: string): TimeRange[] =>
  text
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean)
    .map((s) => {
      const [start, end] = s.split("-").map((x) => x.trim());
      return start && end ? { start, end } : null;
    })
    .filter((r): r is TimeRange => !!r);

const slugify = (s: string): string =>
  s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "") || `svc-${Date.now()}`;

const emptyService = (): BookingService => ({
  id: "",
  name: "",
  description: "",
  durationMin: 60,
  price: 0,
  currency: "usd",
  location: { type: "video", provider: "custom", value: "" },
  active: true,
});

const guessTimezone = (): string => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  } catch {
    return "UTC";
  }
};

const BookingForm = ({ communityId, isActive }: Props) => {
  const config = useBookingConfig({ communityId });

  const [enabled, setEnabled] = useState(false);
  const [timezone, setTimezone] = useState(guessTimezone());
  const [bufferMin, setBufferMin] = useState(0);
  const [minNoticeHours, setMinNoticeHours] = useState(12);
  const [maxAdvanceDays, setMaxAdvanceDays] = useState(60);
  const [notifyEmail, setNotifyEmail] = useState("");
  const [services, setServices] = useState<BookingService[]>([]);
  const [availabilityText, setAvailabilityText] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const [bookings, setBookings] = useState<BookingSummary[]>([]);
  const [loadingBookings, setLoadingBookings] = useState(false);

  // Hydrate local state from the config doc.
  useEffect(() => {
    if (!config) return;
    setEnabled(config.enabled === true);
    setTimezone(config.timezone || guessTimezone());
    setBufferMin(config.bufferMin ?? 0);
    setMinNoticeHours(config.minNoticeHours ?? 12);
    setMaxAdvanceDays(config.maxAdvanceDays ?? 60);
    setNotifyEmail(config.notifyEmail || "");
    setServices(
      (config.services || []).map((s) => ({
        ...emptyService(),
        ...s,
        location: {
          ...emptyService().location,
          ...(s.location || {}),
          provider:
            s.location?.type === "video"
              ? s.location?.provider || "custom"
              : undefined,
        },
      }))
    );
    const at: Record<string, string> = {};
    DAYS.forEach((d) => (at[d] = rangesToText(config.availability?.[d])));
    setAvailabilityText(at);
  }, [config]);

  const timezones = useMemo<string[]>(() => {
    try {
      return (Intl as any).supportedValuesOf
        ? (Intl as any).supportedValuesOf("timeZone")
        : [guessTimezone(), "UTC"];
    } catch {
      return [guessTimezone(), "UTC"];
    }
  }, []);

  const loadBookings = useCallback(async () => {
    if (!communityId) return;
    setLoadingBookings(true);
    try {
      const rows = await listBookings(communityId, { limit: 100 });
      setBookings(rows.filter((b) => b.status === "confirmed" || b.status === "pending_payment"));
    } catch {
      setBookings([]);
    } finally {
      setLoadingBookings(false);
    }
  }, [communityId]);

  useEffect(() => {
    if (isActive) loadBookings();
  }, [isActive, loadBookings]);

  const updateService = (idx: number, patch: Partial<BookingService>) =>
    setServices((prev) => prev.map((s, i) => (i === idx ? { ...s, ...patch } : s)));

  const onSave = async () => {
    if (!communityId) return;
    setSaving(true);
    setError(null);
    setSaved(false);
    try {
      const cleanServices = services
        .filter((s) => s.name.trim())
        .map((s) => ({ ...s, id: s.id || slugify(s.name), price: Number(s.price) || 0, durationMin: Number(s.durationMin) || 30 }));
      const availability: BookingConfig["availability"] = {};
      DAYS.forEach((d) => {
        const ranges = textToRanges(availabilityText[d] || "");
        if (ranges.length) (availability as any)[d] = ranges;
      });
      if (enabled && !cleanServices.length) {
        throw new Error("Add at least one service before enabling booking.");
      }
      await saveBookingConfig(communityId, {
        enabled,
        timezone,
        bufferMin: Number(bufferMin) || 0,
        minNoticeHours: Number(minNoticeHours) || 0,
        maxAdvanceDays: Number(maxAdvanceDays) || 60,
        notifyEmail: notifyEmail.trim() || null,
        services: cleanServices,
        availability,
      });
      setSaved(true);
    } catch (e: any) {
      setError(e?.message || "Could not save booking config");
    } finally {
      setSaving(false);
    }
  };

  const onCancel = async (id: string) => {
    if (!communityId) return;
    try {
      await adminCancelBooking(communityId, id);
      await loadBookings();
    } catch (e: any) {
      setError(e?.message || "Could not cancel booking");
    }
  };

  return (
    <Wrap>
      <Intro>
        Let clients book time with you directly on your website. Define your
        services, set your weekly availability, and payments run on your own
        Stripe account. Turn this on and add a Book page in the Landing tab, or
        it shows on your landing automatically.
      </Intro>

      <GoogleCalendarConnectPanel communityId={communityId} />
      <ZoomConnectPanel communityId={communityId} />

      <Row>
        <label style={{ display: "flex", gap: 8, alignItems: "center", fontFamily: "var(--font-sans)", fontSize: "var(--font-size-sm)" }}>
          <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
          Booking enabled
        </label>
        {config?.enabled ? <Tag>Live</Tag> : <Tag>Off</Tag>}
      </Row>

      <Section>
        <SectionTitle>Scheduling rules</SectionTitle>
        <Row>
          <Field style={{ minWidth: 240 }}>
            <Label>Timezone</Label>
            <Select value={timezone} onChange={(e) => setTimezone(e.target.value)}>
              {timezones.map((tz) => (
                <option key={tz} value={tz}>{tz}</option>
              ))}
            </Select>
          </Field>
          <Field>
            <Label>Buffer (min)</Label>
            <Input type="number" value={bufferMin} onChange={(e: any) => setBufferMin(e.target.value)} />
          </Field>
          <Field>
            <Label>Min notice (hrs)</Label>
            <Input type="number" value={minNoticeHours} onChange={(e: any) => setMinNoticeHours(e.target.value)} />
          </Field>
          <Field>
            <Label>Max advance (days)</Label>
            <Input type="number" value={maxAdvanceDays} onChange={(e: any) => setMaxAdvanceDays(e.target.value)} />
          </Field>
          <Field style={{ minWidth: 220 }}>
            <Label>Notify email (optional)</Label>
            <Input value={notifyEmail} placeholder="you@email.com" onChange={(e: any) => setNotifyEmail(e.target.value)} />
          </Field>
        </Row>
      </Section>

      <Section>
        <SectionTitle>Services</SectionTitle>
        {services.map((s, idx) => (
          <ServiceCard key={idx}>
            <Row>
              <Field style={{ flex: 1, minWidth: 200 }}>
                <Label>Name</Label>
                <Input value={s.name} onChange={(e: any) => updateService(idx, { name: e.target.value })} />
              </Field>
              <Field>
                <Label>Duration (min)</Label>
                <Input type="number" value={s.durationMin} onChange={(e: any) => updateService(idx, { durationMin: e.target.value })} />
              </Field>
              <Field>
                <Label>Price (0 = free)</Label>
                <Input type="number" value={s.price} onChange={(e: any) => updateService(idx, { price: e.target.value })} />
              </Field>
              <Field style={{ minWidth: 90 }}>
                <Label>Currency</Label>
                <Input value={s.currency} onChange={(e: any) => updateService(idx, { currency: e.target.value })} />
              </Field>
            </Row>
            <Row>
              <Field>
                <Label>Location</Label>
                <Select value={s.location.type} onChange={(e) => {
                  const type = e.target.value as any;
                  updateService(idx, {
                    location: {
                      ...s.location,
                      type,
                      provider: type === "video" ? s.location.provider || "custom" : undefined,
                    },
                  });
                }}>
                  <option value="video">Video call</option>
                  <option value="phone">Phone</option>
                  <option value="in_person">In person</option>
                </Select>
              </Field>
              {s.location.type === "video" ? (
                <Field>
                  <Label>Video provider</Label>
                  <Select
                    value={s.location.provider || "custom"}
                    onChange={(e) =>
                      updateService(idx, {
                        location: {
                          ...s.location,
                          provider: e.target.value as any,
                          value: e.target.value === "custom" ? s.location.value : "",
                        },
                      })
                    }
                  >
                    <option value="custom">Custom meeting link</option>
                    <option value="google_meet">Google Meet (automatic)</option>
                    <option value="zoom">Zoom (automatic)</option>
                  </Select>
                </Field>
              ) : null}
              <Field style={{ flex: 1, minWidth: 220 }}>
                <Label>
                  {s.location.type === "video"
                    ? s.location.provider === "google_meet"
                      ? "Generated from Google Calendar"
                      : s.location.provider === "zoom"
                      ? "Generated from connected Zoom"
                      : "Meeting link"
                    : s.location.type === "phone"
                    ? "Phone details"
                    : "Address"}
                </Label>
                <Input
                  value={s.location.value || ""}
                  disabled={
                    s.location.type === "video" &&
                    ["google_meet", "zoom"].includes(s.location.provider || "")
                  }
                  placeholder={
                    s.location.type === "video" && s.location.provider !== "custom"
                      ? "Created for each booking"
                      : undefined
                  }
                  onChange={(e: any) => updateService(idx, { location: { ...s.location, value: e.target.value } })}
                />
              </Field>
              {s.location.type === "video" && s.location.provider === "zoom" ? (
                <Field style={{ maxWidth: 360 }}>
                  <label style={{ display: "flex", gap: 6, alignItems: "center", fontFamily: "var(--font-sans)", fontSize: "var(--font-size-sm)" }}>
                    <input
                      type="checkbox"
                      checked={(s.location as ZoomBookingLocation).joinBeforeHost === true}
                      onChange={(e) =>
                        updateService(idx, {
                          location: { ...s.location, joinBeforeHost: e.target.checked } as ZoomBookingLocation,
                        })
                      }
                    />
                    Clients can join before you
                  </label>
                  <ToggleHint>
                    Clients enter the meeting without waiting for you. This turns off the waiting room. Applies to new bookings. If your Zoom admin locks these settings, Zoom keeps its own.
                  </ToggleHint>
                </Field>
              ) : null}
              <label style={{ display: "flex", gap: 6, alignItems: "center", fontFamily: "var(--font-sans)", fontSize: "var(--font-size-sm)" }}>
                <input type="checkbox" checked={s.active} onChange={(e) => updateService(idx, { active: e.target.checked })} />
                Active
              </label>
              <Button variant="danger" size="sm" onClick={() => setServices((prev) => prev.filter((_, i) => i !== idx))}>
                Remove
              </Button>
            </Row>
          </ServiceCard>
        ))}
        <div>
          <Button variant="secondary" size="sm" onClick={() => setServices((prev) => [...prev, emptyService()])}>
            Add service
          </Button>
        </div>
      </Section>

      <Section>
        <SectionTitle>Weekly availability</SectionTitle>
        <Muted>Enter time ranges per day, e.g. 09:00-12:00, 13:00-17:00. Leave a day blank to close it.</Muted>
        {DAYS.map((d) => (
          <DayRow key={d}>
            <DayLabel>{d}</DayLabel>
            <Input
              value={availabilityText[d] || ""}
              placeholder="09:00-17:00"
              onChange={(e: any) => setAvailabilityText((prev) => ({ ...prev, [d]: e.target.value }))}
            />
          </DayRow>
        ))}
      </Section>

      {error && <Warn>{error}</Warn>}
      <Row>
        <Button variant="primary" onClick={onSave} disabled={saving || !communityId}>
          {saving ? "Saving..." : "Save booking settings"}
        </Button>
        {saved && <Muted>Saved.</Muted>}
      </Row>

      <Section>
        <SectionTitle>Upcoming bookings</SectionTitle>
        <Table>
          {loadingBookings ? (
            <Empty>Loading...</Empty>
          ) : bookings.length === 0 ? (
            <Empty>No upcoming bookings.</Empty>
          ) : (
            bookings.map((b) => (
              <BodyRow key={b.id}>
                <ServiceCell>
                  <span>{b.service?.name || b.serviceId}</span>
                  {b.meetingSyncError && !b.meetingJoinUrl ? (
                    <Tag variant="danger" size="sm" title={bookingSyncErrorText(b)}>
                      No join link
                    </Tag>
                  ) : null}
                  {b.meetingSyncError && b.meetingJoinUrl ? (
                    <Tag variant="warning" size="sm" title={bookingSyncErrorText(b)}>
                      Link not emailed
                    </Tag>
                  ) : null}
                </ServiceCell>
                <span>{new Date(b.startUtc).toLocaleString()}</span>
                <span>
                  {b.client?.name}
                  <br />
                  <Muted>{b.client?.email}</Muted>
                </span>
                <Button variant="danger" size="sm" onClick={() => onCancel(b.id)}>
                  Cancel
                </Button>
              </BodyRow>
            ))
          )}
        </Table>
      </Section>
    </Wrap>
  );
};

export default BookingForm;
